"use client";

import { useState, useEffect } from "react";

const sections = [
  { id: "service-agreements", label: "01. Service Agreements" },
  { id: "payment-terms", label: "02. Payment Terms" },
  { id: "intellectual-property", label: "03. Intellectual Property" },
  { id: "liability", label: "04. Liability" },
];

const STICKY_OFFSET = 112;

const TermsPage = () => {
  const [activeSection, setActiveSection] = useState(sections[0].id);

  useEffect(() => {
    function updateActive() {
      const passed = sections.filter(({ id }) => {
        const el = document.getElementById(`sentinel-${id}`);
        return el && el.getBoundingClientRect().top <= STICKY_OFFSET;
      });
      setActiveSection(
        passed.length > 0 ? passed[passed.length - 1].id : sections[0].id,
      );
    }
    window.addEventListener("scroll", updateActive, { passive: true });
    updateActive();
    return () => window.removeEventListener("scroll", updateActive);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(`sentinel-${id}`);
    if (!el) return;
    const y =
      el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 1;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div className="font-body-md text-on-surface">
      <main className="w-full bg-white">
        <div className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop">
          {/* Hero */}
          <section className="pt-20 pb-20 border-b border-outline-variant">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="max-w-3xl">
                <span className="font-label-sm uppercase tracking-widest text-on-surface-variant mb-4 block">
                  Legal Framework
                </span>
                <h1 className="font-display text-display text-primary mb-6">
                  Terms &amp; Conditions
                </h1>
                <p className="font-body-lg text-on-surface-variant leading-relaxed">
                  The governing framework for architectural precision in digital
                  engineering. Shecraa Studio operates under strict operational
                  mandates covering Web Development, iOS App Development, and
                  Python Automation Solutions. Contact gustavohoze@gmail.com for
                  inquiries.
                </p>
              </div>
              <div className="flex flex-col items-start md:items-end shrink-0">
                <span className="font-label-sm text-on-surface-variant mb-1">
                  Last Updated
                </span>
                <span className="font-body-md font-bold text-primary">
                  May 06, 2026
                </span>
                <a
                  href="/terms.pdf"
                  download
                  className="mt-4 flex items-center gap-2 bg-primary-container text-on-primary px-4 py-2 rounded text-sm font-medium active:scale-95 transition-transform border border-outline-variant"
                >
                  <span className="material-symbols-outlined text-[18px]">
                    download
                  </span>
                  Download PDF
                </a>
              </div>
            </div>
          </section>

          {/* Body */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 pt-20 pb-section-gap">
            {/* Sidebar */}
            <aside className="md:col-span-3 hidden md:block sticky top-28 self-start">
              <div className="border border-outline-variant rounded-lg p-6 bg-surface-container">
                <nav className="flex flex-col gap-1">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="w-full text-left px-3 py-2 rounded text-sm transition-colors"
                      style={
                        activeSection === s.id
                          ? {
                              background: "#ffffff",
                              border: "1.5px solid #111111",
                              color: "#111111",
                              fontWeight: 500,
                            }
                          : {
                              background: "none",
                              border: "1.5px solid transparent",
                              color: "inherit",
                              fontWeight: 400,
                            }
                      }
                    >
                      {s.label}
                    </button>
                  ))}
                </nav>
                <div className="pt-6 mt-6 border-t border-outline-variant">
                  <p className="text-xs text-on-surface-variant leading-tight">
                    For legal inquiries regarding these terms, please contact
                    our compliance desk directly.
                  </p>
                </div>
              </div>
            </aside>

            {/* Article */}
            <article className="md:col-span-9 md:pl-gutter">
              {/* Section 01 */}
              <div className="mb-24">
                <span
                  id="sentinel-service-agreements"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    01
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Service Agreements
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="space-y-4">
                    <h3 className="font-h3 text-h3">Scope Definition</h3>
                    <p className="font-body-md text-on-surface-variant">
                      Project scopes are defined strictly at the commencement.
                      Any "scope creep" will trigger a re-estimation phase and
                      an adjustment to the delivery timeline.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-h3 text-h3">Client Feedback</h3>
                    <p className="font-body-md text-on-surface-variant">
                      Client feedback must be provided within 48 business hours
                      to maintain the architectural integrity of the development
                      sprint.
                    </p>
                  </div>
                </div>
              </div>
              {/* Section 02 */}
              <div className="mb-24">
                <span
                  id="sentinel-payment-terms"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    02
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Payment Terms
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <p className="font-body-md text-on-surface-variant max-w-2xl mb-8">
                  Transparency is central to our code. Every engagement is governed by a custom Statement of Work (SOW) that outlines project-specific pricing. All materials, code, and deliverables are distributed exclusively upon finalization and full payment of the invoice.
                </p>
                <ul className="space-y-4 font-body-md text-on-surface-variant">
                  <li className="flex gap-4">
                    <span className="material-symbols-outlined text-primary text-sm mt-0.5">
                      check_circle
                    </span>
                    <span>
                      <b>Custom Pricing:</b> Detailed in the Statement of Work agreed upon at project inception.
                    </span>
                  </li>
                  <li className="flex gap-4">
                    <span className="material-symbols-outlined text-primary text-sm mt-0.5">
                      check_circle
                    </span>
                    <span>
                      <b>Material Distribution:</b> All deliverables are provided only after payment is finalized and confirmed.
                    </span>
                  </li>
                  <li className="flex gap-4">
                    <span className="material-symbols-outlined text-primary text-sm mt-0.5">
                      check_circle
                    </span>
                    <span>
                      <b>Late Payments:</b> Delay material delivery and may incur
                      penalties or project suspension per the SOW.
                    </span>
                  </li>
                </ul>
              </div>
              {/* Section 03 */}
              <div className="mb-24">
                <span
                  id="sentinel-intellectual-property"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    03
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">
                    Intellectual Property
                  </h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="bg-surface-container p-12 border border-outline-variant space-y-6">
                  <p className="font-body-lg text-primary italic font-medium">
                    &ldquo;Upon final payment, the client receives full ownership of the
                    bespoke code created for their specific instance.&rdquo;
                  </p>
                  <p className="font-body-md text-on-surface-variant">
                    Shecraa Studio retains rights to internal proprietary modules
                    and architectural patterns used across multiple projects. We
                    reserve the right to feature the final product in our
                    portfolio gallery unless a specific NDA (Non-Disclosure
                    Agreement) is executed at project inception.
                  </p>
                </div>
              </div>
              {/* Section 04 */}
              <div className="mb-0">
                <span
                  id="sentinel-liability"
                  className="block h-0 overflow-hidden"
                />
                <div className="flex items-center gap-4 mb-4">
                  <span className="font-label-sm bg-surface-container px-2 py-1 text-on-surface-variant">
                    04
                  </span>
                  <h2 className="font-h2 text-h2 text-primary">Liability</h2>
                </div>
                <div className="h-px bg-outline-variant mb-6" />
                <div className="space-y-6">
                  <p className="font-body-md text-on-surface-variant max-w-2xl">
                    Shecraa Studio builds for performance and stability; however,
                    the digital landscape is fluid. Our liability is strictly
                    limited to the total amount paid by the client for the
                    specific project segment causing the grievance.
                  </p>
                  <div className="border-2 border-primary p-8 space-y-4">
                    <h3 className="font-label-sm uppercase tracking-widest text-primary">
                      Limitation Clause
                    </h3>
                    <p className="font-body-md text-primary font-bold leading-relaxed">
                      UNDER NO CIRCUMSTANCES SHALL SHECRAA STUDIO BE LIABLE FOR
                      INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM
                      SITE DOWNTIME OR THIRD-PARTY API FAILURES POST-DEPLOYMENT.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
